import GuideLayout from "../components/GuideLayout";
import { useLang } from "../i18n/LanguageContext";
import { faqPageLd } from "../lib/schema";

const jsonLd = {
  "@context": "https://schema.org",
  "@type": "Service",
  serviceType: "Private tour and chauffeur transfer",
  name: "Pisa & the Leaning Tower — Private Tour",
  provider: { "@id": "https://yourblacklimo.com/#business" },
  areaServed: ["Pisa", "Piazza dei Miracoli", "Lucca", "Livorno", "Florence", "Tuscany"],
  description:
    "Private Pisa tour to the Leaning Tower and Piazza dei Miracoli in a Mercedes V-Class with an English-speaking chauffeur — from Livorno cruise port, Pisa and Florence airports, or anywhere in Tuscany.",
  url: "https://yourblacklimo.com/pisa-leaning-tower-tour",
};

const CONTENT = {
  en: {
    seoTitle: "Pisa & the Leaning Tower Private Tour | Piazza dei Miracoli — Your Black Limo",
    seoDesc:
      "Private tour to the Leaning Tower of Pisa and Piazza dei Miracoli in a Mercedes V-Class with an English-speaking chauffeur. From Livorno cruise port, Pisa & Florence airports or your hotel — door to door, no queues.",
    overline: "Pisa & the Leaning Tower",
    h1: "Pisa & the Leaning Tower — A Private Chauffeur Day",
    intro:
      "The Leaning Tower and the marble marvels of the Piazza dei Miracoli, without the coach-park crowds. We collect you door-to-door in a Mercedes V-Class with an English-speaking chauffeur and drop you steps from the square — often paired with Lucca or Florence in the same relaxed day.",
    heroAlt: "Black Mercedes V-Class near the Leaning Tower of Pisa and Piazza dei Miracoli",
    blocks: [
      {
        title: "Piazza dei Miracoli",
        body: "A walled meadow of white marble holding four masterpieces — the Leaning Tower, the Cathedral, the Baptistery and the Camposanto. Your chauffeur drops you at the closest access point and is waiting when you're ready, so there's no long walk from a distant coach park.",
      },
      {
        title: "Easy to reach, easy to pair",
        body: "Pisa is about 30 minutes from the Livorno cruise port, minutes from Pisa airport and roughly an hour from Florence. It pairs beautifully with the Renaissance walls of Lucca, or as a first stop on a Florence day.",
      },
    ],
    cards: {
      overline: "The four wonders",
      title: "What you'll see on the square",
      items: [
        { title: "The Leaning Tower", body: "The famous freestanding bell tower — the world's most photographed tilt, and a perfect shot from the lawn." },
        { title: "The Cathedral", body: "Pisa's magnificent striped-marble Duomo, the heart of the Piazza dei Miracoli." },
        { title: "The Baptistery", body: "The largest baptistery in Italy, celebrated for its remarkable interior acoustics." },
        { title: "Lucca nearby", body: "Add the cycle-quiet Renaissance walls of Lucca, just 30 minutes away, for a gentle two-city day." },
      ],
    },
    note: {
      title: "How it works",
      body:
        "Door-to-door pickup from your hotel, villa, the Livorno cruise port or Pisa/Florence airports in a spotless Mercedes V-Class, up to seven guests. Your English-speaking chauffeur handles the driving, parking and timing while you enjoy the square at your own pace. We provide the transportation; entrance tickets to the Tower and monuments, guided visits and meals are booked and paid directly by the client.",
    },
    cta: {
      title: "Plan your private Pisa day",
      secondaryLabel: "See all itineraries",
      secondaryHref: "/cruise-tours",
      primaryLabel: "Request a quote",
      primaryHref: "/#contact",
    },
    faq: {
      overline: "Questions",
      title: "Frequently asked questions",
      items: [
        { q: "How far is Pisa from the Livorno cruise port?", a: "About 30 minutes by car, which makes the Leaning Tower an easy stop on a shore excursion — often combined with Lucca or Florence in the same day." },
        { q: "Do you provide the Leaning Tower tickets?", a: "No — we are a private transport service. Entrance tickets to the Tower and the monuments are booked and paid directly by the client; your chauffeur drives you door-to-door and waits nearby." },
        { q: "Can I combine Pisa with Lucca or Florence?", a: "Yes. Pisa pairs perfectly with Lucca (about 30 minutes) or as a first stop before Florence — your itinerary is built entirely around you." },
        { q: "Is the service private and with an English-speaking driver?", a: "Yes. Every day is fully private in a Mercedes V-Class with a professional, English-speaking chauffeur — never a shared coach." },
      ],
    },
  },
  it: {
    seoTitle: "Tour Privato Pisa e Torre Pendente | Piazza dei Miracoli — Your Black Limo",
    seoDesc:
      "Tour privato alla Torre Pendente di Pisa e Piazza dei Miracoli in Mercedes Classe V con autista che parla inglese. Dal porto crociere di Livorno, dagli aeroporti di Pisa e Firenze o dal tuo hotel — porta a porta, senza code.",
    overline: "Pisa e la Torre Pendente",
    h1: "Pisa e la Torre Pendente — Una Giornata Privata con Autista",
    intro:
      "La Torre Pendente e le meraviglie di marmo della Piazza dei Miracoli, senza la folla dei parcheggi dei pullman. Ti veniamo a prendere porta a porta in Mercedes Classe V con autista che parla inglese e ti lasciamo a due passi dalla piazza — spesso abbinata a Lucca o Firenze nella stessa giornata rilassata.",
    heroAlt: "Mercedes Classe V nera vicino alla Torre Pendente di Pisa e Piazza dei Miracoli",
    blocks: [
      {
        title: "Piazza dei Miracoli",
        body: "Un prato cinto di marmo bianco che custodisce quattro capolavori — la Torre Pendente, il Duomo, il Battistero e il Camposanto. Il tuo autista ti lascia al punto di accesso più vicino e ti aspetta quando sei pronto, senza lunghe camminate da un parcheggio lontano.",
      },
      {
        title: "Facile da raggiungere, facile da abbinare",
        body: "Pisa è a circa 30 minuti dal porto crociere di Livorno, a pochi minuti dall'aeroporto di Pisa e a circa un'ora da Firenze. Si abbina splendidamente alle mura rinascimentali di Lucca, o come prima tappa di una giornata a Firenze.",
      },
    ],
    cards: {
      overline: "Le quattro meraviglie",
      title: "Cosa vedrai sulla piazza",
      items: [
        { title: "La Torre Pendente", body: "Il celebre campanile indipendente — l'inclinazione più fotografata al mondo, e uno scatto perfetto dal prato." },
        { title: "Il Duomo", body: "Il magnifico Duomo di Pisa in marmo striato, cuore della Piazza dei Miracoli." },
        { title: "Il Battistero", body: "Il più grande battistero d'Italia, celebre per la straordinaria acustica interna." },
        { title: "Lucca vicina", body: "Aggiungi le tranquille mura rinascimentali di Lucca, a soli 30 minuti, per una dolce giornata tra due città." },
      ],
    },
    note: {
      title: "Come funziona",
      body:
        "Prelievo porta a porta dal tuo hotel, villa, dal porto crociere di Livorno o dagli aeroporti di Pisa/Firenze in un'impeccabile Mercedes Classe V, fino a sette ospiti. Il tuo autista che parla inglese pensa alla guida, al parcheggio e ai tempi mentre tu goditi la piazza al tuo ritmo. Forniamo il trasporto; i biglietti d'ingresso alla Torre e ai monumenti, le visite guidate e i pasti sono prenotati e pagati direttamente dal cliente.",
    },
    cta: {
      title: "Organizza la tua giornata privata a Pisa",
      secondaryLabel: "Vedi tutti gli itinerari",
      secondaryHref: "/cruise-tours",
      primaryLabel: "Richiedi un preventivo",
      primaryHref: "/#contact",
    },
    faq: {
      overline: "Domande",
      title: "Domande frequenti",
      items: [
        { q: "Quanto dista Pisa dal porto crociere di Livorno?", a: "Circa 30 minuti in auto, il che rende la Torre Pendente una sosta comoda in un'escursione a terra — spesso abbinata a Lucca o Firenze nella stessa giornata." },
        { q: "Fornite i biglietti per la Torre Pendente?", a: "No — siamo un servizio di trasporto privato. I biglietti d'ingresso alla Torre e ai monumenti sono prenotati e pagati direttamente dal cliente; il tuo autista ti accompagna porta a porta e attende nelle vicinanze." },
        { q: "Posso abbinare Pisa a Lucca o Firenze?", a: "Sì. Pisa si abbina perfettamente a Lucca (circa 30 minuti) o come prima tappa prima di Firenze — l'itinerario è costruito interamente su di te." },
        { q: "Il servizio è privato e con autista che parla inglese?", a: "Sì. Ogni giornata è completamente privata in una Mercedes Classe V con autista professionista che parla inglese — mai un pullman condiviso." },
      ],
    },
  },
};

export default function PisaLeaningTower() {
  const { lang } = useLang();
  const c = CONTENT[lang === "it" ? "it" : "en"];
  const { ["@context"]: _ctx, ...serviceNode } = jsonLd;
  const graphLd = { "@context": "https://schema.org", "@graph": [serviceNode, faqPageLd(c.faq.items)] };
  return (
    <GuideLayout
      testid="pisa-leaning-tower-guide"
      faq={c.faq}
      seo={{ title: c.seoTitle, description: c.seoDesc, path: "/pisa-leaning-tower-tour", locale: lang === "it" ? "it_IT" : "en_US", jsonLd: graphLd }}
      overline={c.overline}
      h1={c.h1}
      intro={c.intro}
      heroImage={"https://static.prod-images.emergentagent.com/jobs/c98acbe0-6e23-474e-adcd-5ab166743bcb/images/424b84a00f172ac60bb97ca8783b83a105645a567c807397db917fd152a660a8.jpeg"}
      heroAlt={c.heroAlt}
      blocks={c.blocks}
      cards={c.cards}
      note={c.note}
      cta={c.cta}
    />
  );
}
